import LPressable from "@components/LPressable";
import { useAuthSheet } from "@components/modals/AuthModal/useAuthSheet";
import { AddToPlaylistModalSWMSheet } from "@components/modals/AddToPlaylistModalSWM";
import { useLibrary } from "@hooks/trpc/useLibrary";
import { useSaveHaptics } from "@hooks/useSaveHaptics";
import { theme } from "@theme";
import { isSongSavedInUserPlaylists } from "@utils/collections/playlists";
import { useAuth } from "@utils/firebaseAuth/auth";
import type { CatalogSong } from "@utils/player";
import { useState } from "react";
import type { ViewStyle } from "react-native";
import { useBottomSheetManager } from "react-native-bottom-sheet-stack";
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withSequence,
  withSpring,
  withTiming,
} from "react-native-reanimated";
import SaveIcon from "./SaveIcon";

const SAVE_ICON_SWAP_DELAY_MS = 55;
const SAVE_SHEET_DELAY_MS = 280;

const SaveButton = ({
  song,
  style,
}: {
  song: CatalogSong;
  style?: ViewStyle;
}) => {
  const { firebaseUser, status: authStatus } = useAuth();
  const openAuthSheet = useAuthSheet();
  const bottomSheetManager = useBottomSheetManager();
  const [saveAnimationTrigger, setSaveAnimationTrigger] = useState(0);
  const { playExistingSave, playNewSave } = useSaveHaptics();
  const saveIconScale = useSharedValue(1);
  const saveIconAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: saveIconScale.value }],
  }));
  const {
    isLiked,
    query: { data: library },
    toggleSongLike,
  } = useLibrary();
  const liked = isLiked({ id: song.id, type: "song" });
  const saved = isSongSavedInUserPlaylists(
    library?.filter((item) => item.type === "playlist"),
    song.id,
    firebaseUser?.uid,
  );

  const handleSave = () => {
    if (authStatus !== "logged-in") {
      openAuthSheet();
      return;
    }

    if (saved) {
      playExistingSave();
    } else {
      playNewSave();
    }

    saveIconScale.set(
      withSequence(
        withTiming(0.72, {
          duration: SAVE_ICON_SWAP_DELAY_MS,
          easing: Easing.out(Easing.quad),
        }),
        withSpring(1, { damping: 9, mass: 0.35, stiffness: 360 }),
      ),
    );

    if (!saved) {
      setSaveAnimationTrigger((trigger) => trigger + 1);
    }

    if (!saved && !liked) {
      setTimeout(() => toggleSongLike(song), SAVE_ICON_SWAP_DELAY_MS);
    }

    const openSaveSheet = () => {
      bottomSheetManager.open(<AddToPlaylistModalSWMSheet song={song} />);
    };

    if (saved) {
      openSaveSheet();
    } else {
      setTimeout(openSaveSheet, SAVE_SHEET_DELAY_MS);
    }
  };

  return (
    <LPressable
      accessibilityLabel={saved ? "Saved in" : "Save song"}
      hitSlop={{ top: 12, bottom: 12 }}
      onPress={handleSave}
      onPressIn={() => {
        saveIconScale.set(
          withTiming(0.84, {
            duration: 90,
            easing: Easing.out(Easing.quad),
          }),
        );
      }}
      onPressOut={() => {
        saveIconScale.set(
          withSpring(1, { damping: 14, mass: 0.35, stiffness: 360 }),
        );
      }}
      sentryEventName={`music_player_${saved ? "open_saved_in" : "save_song"}`}
      style={{ paddingHorizontal: theme.spacing.s2_5, ...style }}
    >
      <Animated.View style={saveIconAnimatedStyle}>
        <SaveIcon animationTrigger={saveAnimationTrigger} saved={saved} />
      </Animated.View>
    </LPressable>
  );
};

export default SaveButton;
